import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash, ArrowsClockwise, Copy } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const SOURCE_LABELS = { airbnb: "Airbnb", booking: "Booking", otro: "Otro" };

export const CalendarsTab = () => {
  const [feeds, setFeeds] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [form, setForm] = useState({ apartment_slug: "", source: "airbnb", url: "" });
  const [syncing, setSyncing] = useState(null);

  const load = () => api.get("/admin/ical/feeds").then((r) => setFeeds(r.data)).catch(() => setFeeds([]));
  useEffect(() => {
    load();
    api.get("/apartments").then((r) => {
      setApartments(r.data);
      if (r.data.length) setForm((f) => ({ ...f, apartment_slug: f.apartment_slug || r.data[0].slug }));
    }).catch(() => {});
  }, []);

  const add = async () => {
    try {
      await api.post("/admin/ical/feeds", form);
      toast.success("Calendario conectado y sincronizado");
      setForm((f) => ({ ...f, url: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo conectar el calendario");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/ical/feeds/${id}`);
      toast.success("Calendario desconectado. Sus fechas importadas quedan libres.");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const syncNow = async (id) => {
    setSyncing(id);
    try {
      const { data } = await api.post(`/admin/ical/feeds/${id}/sync`);
      if (data.ok) toast.success(`Sincronizado: ${data.count} evento(s) importados`);
      else toast.error(`Error al sincronizar: ${data.error}`);
      load();
    } catch {
      toast.error("No se pudo sincronizar");
    }
    setSyncing(null);
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("URL copiada"));
  };

  if (!feeds) return <p className="text-[#A0A3A9] text-sm p-6">Cargando calendarios…</p>;

  return (
    <div data-testid="calendars-tab" className="flex flex-col gap-6">
      {/* Exportar: pegar estas URLs en Airbnb/Booking → "Importar calendario" */}
      <div className="bg-[#181A1F] border border-[#2A2D35] p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1">Tus calendarios para Airbnb / Booking</p>
        <p className="text-xs text-[#A0A3A9] mb-4">Copia la URL de cada piso y pégala en el anuncio de Airbnb/Booking (sección «Importar calendario»). Así esas plataformas bloquean las reservas hechas en tu web.</p>
        <div className="flex flex-col divide-y divide-[#2A2D35]">
          {apartments.map((a) => (
            <div key={a.slug} className="py-3 flex items-center justify-between gap-3 flex-wrap">
              <span className="text-sm text-[#FDFBF7]/85 font-medium">{a.name}</span>
              <div className="flex items-center gap-2 min-w-0">
                <code data-testid={`ical-export-${a.slug}`} className="text-xs text-[#A0A3A9] bg-[#0F1115] border border-[#2A2D35] px-2.5 py-1.5 truncate max-w-[420px]">
                  {`${window.location.origin}/api/ical/${a.slug}.ics`}
                </code>
                <button data-testid={`ical-copy-${a.slug}`} onClick={() => copy(`${window.location.origin}/api/ical/${a.slug}.ics`)} className="p-2 border border-[#2A2D35] text-[#A0A3A9] hover:text-[#FDFBF7] hover:border-[#D5C0A1] transition-colors shrink-0" aria-label="Copiar URL">
                  <Copy size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Importar: URLs de Airbnb/Booking → la web bloquea esas fechas */}
      <div className="bg-[#181A1F] border border-[#2A2D35] p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1">Conectar calendario de Airbnb / Booking</p>
        <p className="text-xs text-[#A0A3A9] mb-4">Pega aquí la URL de «Exportar calendario» de cada anuncio. Tu web bloqueará esas reservas automáticamente (se revisan cada 20 minutos).</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">Apartamento</label>
            <select data-testid="ical-apartment" value={form.apartment_slug} onChange={(e) => setForm({ ...form, apartment_slug: e.target.value })} className="bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5 min-w-[170px]">
              {apartments.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">Plataforma</label>
            <select data-testid="ical-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5">
              {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[260px]">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9] mb-1.5">URL del calendario (.ics)</label>
            <input data-testid="ical-url" className="w-full bg-[#0F1115] border border-[#2A2D35] text-[#FDFBF7] text-sm px-3 py-2.5" placeholder="https://es.airbnb.com/calendar/ical/…" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          </div>
          <button data-testid="ical-add" onClick={add} disabled={!form.url.trim()} className="px-5 py-2.5 bg-[#D5C0A1] text-[#0F1115] text-xs uppercase tracking-[0.15em] hover:bg-[#FDFBF7] transition-colors disabled:opacity-40">
            Conectar
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {feeds.length === 0 && <p className="text-[#A0A3A9] text-sm p-6 bg-[#181A1F] border border-[#2A2D35]">No hay calendarios conectados todavía.</p>}
        {feeds.map((f) => (
          <div key={f.id} data-testid={`ical-feed-${f.id.slice(0, 8)}`} className="bg-[#181A1F] border border-[#2A2D35] p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-[#FDFBF7]/85 min-w-0">
              <p>
                <span className="font-medium">{f.apartment_name}</span>
                <span className="text-xs text-[#A0A3A9] ml-2 uppercase tracking-[0.1em]">{SOURCE_LABELS[f.source] || f.source}</span>
              </p>
              <p className="text-xs text-[#A0A3A9] mt-1 truncate max-w-[520px]">{f.url}</p>
              <p className="text-[10px] text-[#A0A3A9]/70 mt-1">
                {f.last_sync
                  ? `Última sync: ${new Date(f.last_sync).toLocaleString("es-ES")} · ${f.last_count ?? 0} evento(s)`
                  : "Aún no sincronizado"}
                {f.last_error && <span className="text-[#E08A6C]"> · Error: {f.last_error}</span>}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`ical-sync-${f.id.slice(0, 8)}`} onClick={() => syncNow(f.id)} disabled={syncing === f.id} className="p-2 border border-[#2A2D35] text-[#9DC4A8] hover:border-[#9DC4A8] transition-colors disabled:opacity-40" aria-label="Sincronizar ahora">
                <ArrowsClockwise size={15} />
              </button>
              <button data-testid={`ical-delete-${f.id.slice(0, 8)}`} onClick={() => remove(f.id)} className="p-2 border border-[#2A2D35] text-[#E08A6C] hover:border-[#E08A6C] transition-colors" aria-label="Desconectar calendario">
                <Trash size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
